import { useState } from "react";
import { PackageSearch, RefreshCcw, CreditCard, MessageCircle, ArrowRight, LogIn, Download, Ban } from "lucide-react";
import { api } from "@/lib/api";
import { SITE } from "@/lib/seo";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const WA_HELP = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(
  "Hi Kurti Connection! I need help with a return/exchange for my order."
)}`;

const NON_RETURNABLE = ["Leggings", "Jewelry", "Altered clothing", "Used / worn clothing", "Dupattas"];

const CARDS = [
  {
    icon: PackageSearch,
    title: "Track your order",
    text: "Sign in with the email you used at checkout — no password needed, we email you a code. See live status, tracking number and delivery updates for every order.",
    cta: "Track My Order",
    href: SITE.accountUrl,
    testid: "track-order-cta",
  },
  {
    icon: RefreshCcw,
    title: "Request a return or exchange",
    text: "Use the form below — tell us what you're sending back, choose store credit or a refund, and we email you a prepaid shipping label once it's accepted.",
    cta: "Start a Return Request",
    href: "#request",
    testid: "start-return-cta",
  },
  {
    icon: CreditCard,
    title: "Get store credit",
    text: "Once we approve your return, your store credit (minus the $5 label fee) lands on your account — use it on anything, anytime.",
    cta: "Check My Store Credit",
    href: SITE.accountUrl,
    testid: "store-credit-cta",
  },
];

const BLANK = {
  order_number: "", name: "", email: "", phone: "",
  street1: "", street2: "", city: "", state: "", zip_code: "",
  items: "", reason: "Wrong size", resolution: "store_credit",
  eligible_confirmed: false, fee_acknowledged: false,
};

export default function ReturnsExchange() {
  const [form, setForm] = useState(BLANK);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const r = await api.post("/returns", form);
      setResult(r.data);
      toast.success("Return request received.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please try again or WhatsApp us.");
    } finally { setSending(false); }
  };

  const inputCls = "w-full border border-[#E5E5DF] px-3 py-2.5 text-sm font-body focus:outline-none focus:border-[#1A1A1A] bg-white";

  return (
    <main data-testid="returns-page">
      <Seo
        title="Returns, Exchanges & Store Credit"
        description="Track your Kurti Connection order, request a return or exchange within 14 days, choose store credit or a refund, and receive a prepaid return label by email once accepted."
        path="/returns"
      />

      {/* Header */}
      <section className="bg-[#1A1A1A] text-[#FFFFFF]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12 py-14 sm:py-20">
          <div className="overline text-[#B84A3B] mb-3">Easy &amp; Self-Serve</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.98]">Returns, Exchanges &amp; Store Credit</h1>
          <p className="mt-5 text-base text-[#FFFFFF]/80 font-body leading-relaxed max-w-2xl">
            Changed your mind or need a different size? Request a return within <b>14 days of delivery</b>,
            choose store credit or a refund to your original card, and we'll email your prepaid shipping label
            as soon as your return is accepted.
          </p>
          <a href={SITE.accountUrl} target="_blank" rel="noreferrer" data-testid="header-account-cta"
            className="inline-flex items-center gap-2 mt-8 bg-[#FFFFFF] text-[#1A1A1A] px-7 py-3.5 text-[0.72rem] font-mono tracking-[0.18em] uppercase hover:bg-[#B84A3B] hover:text-white transition-colors">
            <LogIn size={15} /> Sign In to My Account
          </a>
        </div>
      </section>

      {/* Three self-serve cards */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-14 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="returns-cards">
        {CARDS.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.08}>
            <div className="border border-[#E5E5DF] p-7 h-full flex flex-col" data-testid={`card-${c.testid}`}>
              <c.icon size={26} strokeWidth={1.4} className="text-[#B84A3B]" />
              <h2 className="font-display text-2xl tracking-tight mt-4">{c.title}</h2>
              <p className="text-sm text-[#6E6E69] font-body leading-relaxed mt-2 flex-1">{c.text}</p>
              {c.href.startsWith("#") ? (
                <a href={c.href} data-testid={c.testid}
                  className="inline-flex items-center gap-2 mt-6 text-[0.68rem] font-mono tracking-[0.18em] uppercase text-[#1A1A1A] hover:text-[#B84A3B] transition-colors">
                  {c.cta} <ArrowRight size={13} />
                </a>
              ) : (
                <a href={c.href} target="_blank" rel="noreferrer" data-testid={c.testid}
                  className="inline-flex items-center gap-2 mt-6 text-[0.68rem] font-mono tracking-[0.18em] uppercase text-[#1A1A1A] hover:text-[#B84A3B] transition-colors">
                  {c.cta} <ArrowRight size={13} />
                </a>
              )}
            </div>
          </Reveal>
        ))}
      </section>

      {/* Non-returnable rules */}
      <section className="border-y border-[#E5E5DF] bg-[#F7F7F4]" data-testid="return-rules">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-12">
          <div className="flex items-start gap-4">
            <Ban size={24} strokeWidth={1.4} className="text-[#B84A3B] shrink-0 mt-1" />
            <div>
              <h2 className="font-display text-3xl tracking-tight">Please check before you request</h2>
              <p className="text-sm text-[#6E6E69] font-body mt-2 max-w-2xl leading-relaxed">
                For hygiene and quality reasons, we <b>cannot accept returns</b> on:
              </p>
              <ul className="mt-4 flex flex-wrap gap-2" data-testid="non-returnable-list">
                {NON_RETURNABLE.map((r) => (
                  <li key={r} className="border border-[#B84A3B]/40 text-[#B84A3B] text-[0.7rem] font-mono tracking-[0.12em] uppercase px-3 py-1.5">{r}</li>
                ))}
              </ul>
              <p className="text-xs text-[#6E6E69] font-body mt-5 max-w-2xl leading-relaxed">
                Everything else can come back within <b>14 days of delivery</b>, unworn with tags on.
                A <b>$5 return shipping fee</b> covers your prepaid label and is deducted from your refund or store credit.
                Full details in our <Link to="/policies/refund-policy" className="underline underline-offset-2 hover:text-[#B84A3B]" data-testid="refund-policy-link">Return &amp; Refund Policy</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Request form */}
      <section id="request" className="max-w-[760px] mx-auto px-5 sm:px-8 py-14" data-testid="return-form-section">
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Start your return request</h2>
        <p className="text-sm text-[#6E6E69] font-body mt-2">Takes under a minute — we review it and email your prepaid label once accepted.</p>

        {result ? (
          <div className="border border-[#E5E5DF] p-7 mt-8" data-testid="return-success">
            <h3 className="font-display text-2xl">Request received{result.duplicate ? " (already on file)" : ""}</h3>
            {result.label_ready ? (
              <>
                <p className="text-sm text-[#6E6E69] font-body mt-3">
                  Your prepaid USPS label is ready{result.tracking_number ? <> — tracking <b>{result.tracking_number}</b></> : null}.
                  Print it, attach it to your package and drop it at any USPS location. A copy was emailed to you.
                </p>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/returns/${result.id}/label`} target="_blank" rel="noreferrer"
                  data-testid="download-label-button"
                  className="inline-flex items-center gap-2 mt-6 bg-[#1A1A1A] text-white px-8 py-4 text-[0.72rem] font-mono tracking-[0.2em] uppercase hover:bg-[#B84A3B] transition-colors">
                  <Download size={15} /> Download Return Label
                </a>
              </>
            ) : (
              <p className="text-sm text-[#6E6E69] font-body mt-3">
                We'll review your request and email your prepaid USPS shipping label once it's accepted —
                usually within 1 business day. Questions in the meantime?{" "}
                <a href={WA_HELP} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-[#B84A3B]">WhatsApp us</a>.
              </p>
            )}
            <p className="text-xs text-[#6E6E69] font-body mt-5">
              Once we receive and approve your return, your {result.resolution === "store_credit" || form.resolution === "store_credit" ? "store credit" : "refund to your original card"} is issued minus the $5 label fee.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="return-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label><span className="overline text-[#1A1A1A] block mb-1.5">Order number *</span>
                <input required value={form.order_number} onChange={set("order_number")} placeholder="e.g. 1042 or #1042" data-testid="return-order-number" className={inputCls} /></label>
              <label><span className="overline text-[#1A1A1A] block mb-1.5">Full name *</span>
                <input required value={form.name} onChange={set("name")} data-testid="return-name" className={inputCls} /></label>
              <label><span className="overline text-[#1A1A1A] block mb-1.5">Email *</span>
                <input required type="email" value={form.email} onChange={set("email")} data-testid="return-email" className={inputCls} /></label>
              <label><span className="overline text-[#1A1A1A] block mb-1.5">Phone *</span>
                <input required type="tel" value={form.phone} onChange={set("phone")} placeholder="Needed for the label" data-testid="return-phone" className={inputCls} /></label>
            </div>

            <div>
              <span className="overline text-[#1A1A1A] block mb-1.5">Your address (the label ships FROM here) *</span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input required value={form.street1} onChange={set("street1")} placeholder="Street address" data-testid="return-street1" className={`${inputCls} sm:col-span-2`} />
                <input value={form.street2} onChange={set("street2")} placeholder="Apt / suite (optional)" data-testid="return-street2" className={`${inputCls} sm:col-span-2`} />
                <input required value={form.city} onChange={set("city")} placeholder="City" data-testid="return-city" className={inputCls} />
                <div className="grid grid-cols-2 gap-4">
                  <input required value={form.state} onChange={set("state")} placeholder="State (IL)" maxLength={2} data-testid="return-state" className={inputCls} />
                  <input required value={form.zip_code} onChange={set("zip_code")} placeholder="ZIP" data-testid="return-zip" className={inputCls} />
                </div>
              </div>
            </div>

            <label className="block"><span className="overline text-[#1A1A1A] block mb-1.5">What are you returning? *</span>
              <textarea required rows={2} value={form.items} onChange={set("items")} placeholder="e.g. Navy Anarkali Gown — size M" data-testid="return-items" className={inputCls} /></label>

            <label className="block"><span className="overline text-[#1A1A1A] block mb-1.5">Reason</span>
              <select value={form.reason} onChange={set("reason")} data-testid="return-reason" className={inputCls}>
                <option>Wrong size</option>
                <option>Changed my mind</option>
                <option>Damaged / defective</option>
                <option>Not as expected</option>
                <option>Other</option>
              </select></label>

            <div>
              <span className="overline text-[#1A1A1A] block mb-2">How should we complete your return? *</span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  ["store_credit", "Store credit", "Fastest — credit lands on your account, spend it on anything"],
                  ["refund", "Refund to original card", "Back to the card you paid with, after we receive the item"],
                ].map(([val, title, sub]) => (
                  <label key={val} data-testid={`resolution-${val}`}
                    className={`border p-4 cursor-pointer transition-colors ${form.resolution === val ? "border-[#1A1A1A] bg-[#F7F7F4]" : "border-[#E5E5DF] hover:border-[#1A1A1A]"}`}>
                    <input type="radio" name="resolution" value={val} checked={form.resolution === val} onChange={set("resolution")} className="sr-only" />
                    <span className="font-body text-sm font-semibold block">{title}</span>
                    <span className="text-xs text-[#6E6E69] font-body mt-1 block">{sub}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-3 border border-[#E5E5DF] p-4">
              <label className="flex items-start gap-3 cursor-pointer" data-testid="return-eligible-check">
                <input type="checkbox" checked={form.eligible_confirmed} onChange={set("eligible_confirmed")} className="w-5 h-5 mt-0.5 accent-[#B84A3B]" />
                <span className="text-[0.8rem] font-body text-[#1A1A1A]">My return does <b>not</b> include leggings, jewelry, altered clothing, used/worn items or dupattas, and it's within 14 days of delivery.</span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="return-fee-check">
                <input type="checkbox" checked={form.fee_acknowledged} onChange={set("fee_acknowledged")} className="w-5 h-5 mt-0.5 accent-[#B84A3B]" />
                <span className="text-[0.8rem] font-body text-[#1A1A1A]">I understand a <b>$5 return shipping fee</b> will be deducted from my {form.resolution === "store_credit" ? "store credit" : "refund"}.</span>
              </label>
            </div>

            <button type="submit" disabled={sending || !form.eligible_confirmed || !form.fee_acknowledged} data-testid="return-submit"
              className="w-full bg-[#1A1A1A] text-white py-4 text-[0.72rem] font-mono tracking-[0.22em] uppercase hover:bg-[#B84A3B] transition-colors disabled:opacity-50">
              {sending ? "Submitting…" : "Submit Return Request"}
            </button>
          </form>
        )}
      </section>

      {/* Personal help */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-16 text-center" data-testid="returns-help">
        <h2 className="font-display text-3xl tracking-tight">Rather talk to a human?</h2>
        <p className="text-sm text-[#6E6E69] font-body mt-3 max-w-md mx-auto">
          Message us your order number on WhatsApp and we'll handle the return, exchange or store credit for you personally.
        </p>
        <a href={WA_HELP} target="_blank" rel="noreferrer" data-testid="returns-whatsapp-cta"
          className="inline-flex items-center gap-2 mt-6 bg-[#25D366] text-[#0b3d1e] px-8 py-4 text-[0.72rem] font-mono tracking-[0.18em] uppercase hover:opacity-90 transition-opacity">
          <MessageCircle size={15} /> WhatsApp Us
        </a>
      </section>
    </main>
  );
}
